// Price + price-history for the Matches cards and quick view (Haim runtime round 3).
//
// PORTED from components/_dashboard/listingsMatch/ListingPriceWithHistory.tsx —
// specifically getPriceChangeInfo ("previous price = latest history entry whose price
// differs from the current one") and the dated-rows popover content — REBUILT to the
// CRM conventions (Hebrew inline, no i18n/useSettings, pastel chip styling per Haim's
// screenshot): bold price, a small soft chip with a trend arrow + %-change (blue =
// drop, orange = rise), and the whole cluster is clickable to open the history.
//
// Data semantics (verified server-side, yad2Shared.service.ts): priceHistory holds the
// PRIOR prices only — createOrUpdate pushes the old price when it detects a change, so
// the CURRENT price is NOT in the array. The popover therefore renders the history
// entries (struck through, dated) and appends the current price as the final bold row.
// No price change → plain bold price, not clickable.
import { useState } from "react";
import type { KeyboardEvent, MouseEvent } from "react";
import { Box, Stack, Divider, Popover, Tooltip, IconButton, Typography } from "@material-ui/core";
import { alpha, useTheme } from "@material-ui/core/styles";
import { Close, TrendingUp, TrendingDown } from "@material-ui/icons";
import type { PriceHistoryEntry } from "../../../../../../@types/sharedSchema";
import { coerceDate, formatShortDate, formatPriceFull } from "./matchesDisplay";

type NormalizedEntry = { price: number; date: Date | null };

/** Valid prior-price entries, oldest→newest (search hits keep dates as epoch millis). */
function normalizeHistory(priceHistory: PriceHistoryEntry[] | undefined): NormalizedEntry[] {
  return (priceHistory ?? [])
    .filter((entry) => typeof entry?.price === "number" && isFinite(entry.price) && entry.price > 0)
    .map((entry) => ({
      price: entry.price,
      date: coerceDate(entry.date as unknown as Date | number | string)
    }))
    .sort((a, b) => (a.date?.getTime() ?? 0) - (b.date?.getTime() ?? 0));
}

/** Ported change detection: previous = most recent history entry that differs. */
function getPriceChangeInfo(price: number, history: NormalizedEntry[]) {
  const previousPrice = [...history].reverse().find((entry) => entry.price !== price)?.price;
  const hasPriceChange = previousPrice !== undefined;
  const priceChangePercent = hasPriceChange
    ? Math.ceil(Math.abs(((price - previousPrice) / previousPrice) * 100))
    : 0;
  const isPriceIncrease = hasPriceChange && price > (previousPrice as number);
  return { hasPriceChange, priceChangePercent, isPriceIncrease };
}

type ListingPriceWithHistoryProps = {
  price: number;
  priceHistory?: PriceHistoryEntry[];
  /** When the CURRENT price took effect — shown on the popover's final row. */
  lastPriceChangeDate?: Date | number | string;
  /** Typography variant for the price text: cards pass subtitle2/subtitle1, quick view h5. */
  variant?: "subtitle2" | "subtitle1" | "h5";
};

export default function ListingPriceWithHistory({
  price,
  priceHistory,
  lastPriceChangeDate,
  variant = "subtitle1"
}: ListingPriceWithHistoryProps): JSX.Element | null {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  if (typeof price !== "number" || !isFinite(price) || price <= 0) return null;

  const history = normalizeHistory(priceHistory);
  const { hasPriceChange, priceChangePercent, isPriceIncrease } = getPriceChangeInfo(
    price,
    history
  );

  if (!hasPriceChange) {
    return (
      <Typography variant={variant} sx={{ fontWeight: 700 }}>
        {formatPriceFull(price)}
      </Typography>
    );
  }

  const chipColor = isPriceIncrease ? theme.palette.warning : theme.palette.info;
  const TrendIcon = isPriceIncrease ? TrendingUp : TrendingDown;
  const changedAt = coerceDate(lastPriceChangeDate) ?? history[history.length - 1]?.date ?? null;

  const openPopover = (target: HTMLElement): void => setAnchorEl(target);
  const close = (): void => setAnchorEl(null);

  const handleClick = (e: MouseEvent<HTMLElement>): void => {
    // Lives inside clickable cards — the price must never open the quick view.
    e.preventDefault();
    e.stopPropagation();
    openPopover(e.currentTarget);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLElement>): void => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      e.stopPropagation();
      openPopover(e.currentTarget);
    }
  };

  return (
    <>
      <Tooltip title="היסטוריית מחיר">
        <Box
          role="button"
          tabIndex={0}
          aria-label="היסטוריית מחיר"
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 0.75,
            maxWidth: "100%",
            cursor: "pointer",
            borderRadius: 0.75,
            "&:hover": { bgcolor: "action.hover" }
          }}
        >
          <Typography
            variant={variant}
            sx={{
              fontWeight: 700,
              textDecorationLine: "underline",
              textDecorationStyle: "dotted",
              textDecorationColor: alpha(theme.palette.text.primary, 0.35),
              textUnderlineOffset: 3
            }}
          >
            {formatPriceFull(price)}
          </Typography>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.25,
              px: 0.75,
              py: 0.1,
              borderRadius: 999,
              color: chipColor.dark,
              bgcolor: alpha(chipColor.main, 0.12),
              border: `1px solid ${alpha(chipColor.main, 0.32)}`
            }}
          >
            <TrendIcon sx={{ fontSize: 13 }} />
            <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.4 }}>
              {isPriceIncrease ? "+" : "-"}
              {priceChangePercent}%
            </Typography>
          </Box>
        </Box>
      </Tooltip>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Stack spacing={0.75} sx={{ p: 1.5, minWidth: 220, maxWidth: 300 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">היסטוריית מחיר</Typography>
            <IconButton size="small" onClick={close}>
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>

          {/* prior prices, oldest → newest, struck through */}
          {history.map((entry, index) => (
            <Stack
              key={`${entry.price}-${entry.date?.getTime() ?? index}`}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <Typography
                variant="body2"
                sx={{ textDecoration: "line-through", color: "text.secondary" }}
              >
                {formatPriceFull(entry.price)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {formatShortDate(entry.date)}
              </Typography>
            </Stack>
          ))}

          <Divider sx={{ borderStyle: "dashed" }} />

          {/* the current price — NOT part of priceHistory, appended as the final row */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" gap={2}>
            <Stack direction="row" alignItems="center" gap={0.75}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {formatPriceFull(price)}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 700, color: chipColor.dark }}>
                {isPriceIncrease ? "+" : "-"}
                {priceChangePercent}%
              </Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {changedAt ? formatShortDate(changedAt) : "מחיר נוכחי"}
            </Typography>
          </Stack>
        </Stack>
      </Popover>
    </>
  );
}
